import { Badge, Card, CardContent, IconButton, Typography, Box, List, ListItem, ListItemText, Button, Divider, Popover, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import React, { useRef, useState } from 'react';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CommentIcon from '@mui/icons-material/Comment';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications, GeneralNotification, ChatNotification } from '../../hooks/useNotifications';

const Notification = () => {
  const notificationRef = useRef(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [commentDialog, setCommentDialog] = useState<{
    open: boolean;
    comment: string;
    message: string;
  }>({
    open: false,
    comment: '',
    message: '',
  });

  const navigate = useNavigate();
  const { notifications, loading, markGeneralNotificationsAsRead, markChatNotificationsAsRead, markAllNotificationsAsRead, fetchAllNotifications } = useNotifications();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // Force refresh when opening notification panel
    fetchAllNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleMarkAllRead = async () => {
    await markAllNotificationsAsRead();
  };

  const handleGeneralNotificationClick = async (notification: GeneralNotification) => {
    // Mark this specific notification as read
    await markGeneralNotificationsAsRead([notification.id]);
    handleClose();

    // For rating requests, navigate to the person who needs to be rated (from_user_id)
    const targetUserId = notification.from_user_id;
    const targetPath = targetUserId ? `/user-artifact-detail/${targetUserId}` : '/artifact-detail';

    // Use department field to determine evaluation type (more reliable than message parsing)
    const department = notification.department?.toUpperCase();
    let evaluationType = '';

    if (department === 'BU') {
      evaluationType = 'BU';
    } else if (department === 'COMPANY') {
      evaluationType = 'Company';
    } else if (department === 'PROJECT') {
      evaluationType = 'Project';
    } else if (department === 'RM') {
      evaluationType = 'RM';
    } else if (department === 'HR') {
      evaluationType = 'HR';
    }

    // Build navigation state with quarter and evaluation info
    const navigationState: any = {};

    // Only pass quarter if it exists
    if (notification.quarter) {
      navigationState.quarter = notification.quarter;
    }

    if (evaluationType) {
      navigationState.openEvaluationTab = true;
      navigationState.evaluationType = evaluationType;
      navigationState.notificationMessage = notification.message;
    }

    // Navigate first
    navigate(targetPath, { state: navigationState });

    // If there's a comment, show the popup after a short delay to ensure navigation completes
    if (notification.comment && notification.comment.trim()) {
      setTimeout(() => {
        setCommentDialog({
          open: true,
          comment: notification.comment,
          message: notification.message,
        });
      }, 100);
    }
  };

  const handleChatNotificationClick = async (notification: ChatNotification) => {
    // Mark this specific chat notification as read
    await markChatNotificationsAsRead([notification.artifact_id]);

    // Navigate to the artifact with chat open
    navigate(`/artifacts/viewArtifact/${notification.artifact_id}`, { state: { showChat: true } });
    handleClose();
  };

  const formatNotificationTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Recently';
    }
  };

  const handleCloseCommentDialog = () => {
    setCommentDialog({
      open: false,
      comment: '',
      message: '',
    });
  };

  const { generalNotifications, chatNotifications, totalCount } = notifications;

  // Calculate if there are any unread notifications
  const hasUnreadNotifications = generalNotifications.some((n) => !n.is_read) || chatNotifications.some((n) => !n.read);

  return (
    <Box position="relative" display="inline-block" ref={notificationRef}>
      <IconButton
        size="large"
        edge="end"
        aria-label="notifications"
        color="inherit"
        sx={{
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        }}
        onClick={handleClick}
      >
        <Badge
          badgeContent={totalCount}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: 10,
              height: 16,
              width: 16,
              borderRadius: '50%',
            },
          }}
        >
          <NotificationsNoneOutlinedIcon fontSize="large" />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          boxShadow: 1,
          borderRadius: '50px',
        }}
      >
        <Card
          sx={{
            width: 500,
          }}
        >
          <CardContent sx={{ p: 2, maxHeight: 600, overflowY: 'auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Notifications ({generalNotifications.length + chatNotifications.length})
              </Typography>
              {hasUnreadNotifications && (
                <Button size="small" onClick={handleMarkAllRead} disabled={loading}>
                  Mark all as read
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 1 }} />

            {generalNotifications.length + chatNotifications.length === 0 ? (
              <Typography variant="body1" sx={{ textAlign: 'center', mt: 2, fontWeight: 'bold' }}>
                No notifications
              </Typography>
            ) : (
              <List>
                {/* General Notifications */}
                {generalNotifications.map((notification) => (
                  <React.Fragment key={`general-${notification.id}`}>
                    <ListItem
                      sx={{
                        backgroundColor: notification.is_read ? 'transparent' : 'rgba(25, 118, 210, 0.05)',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: notification.is_read ? 'rgba(0, 0, 0, 0.05)' : 'rgba(25, 118, 210, 0.1)',
                          transform: 'translateY(-1px)',
                          boxShadow: notification.is_read ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(25, 118, 210, 0.2)',
                        },
                        width: '100%',
                        mb: 1,
                        borderRadius: 1,
                        border: notification.is_read ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(25, 118, 210, 0.2)',
                        transition: 'all 0.2s ease-in-out',
                        position: 'relative',
                      }}
                      onClick={() => handleGeneralNotificationClick(notification)}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Chip label={notification.department ? `${notification.department} Request` : 'Rating Request'} size="small" color={notification.is_read ? 'default' : 'primary'} variant="outlined" />
                                {notification.comment && notification.comment.trim() && (
                                  <Chip label="Has Comment" size="small" color="secondary" variant="outlined" icon={<CommentIcon sx={{ fontSize: '0.8rem !important' }} />} sx={{ fontSize: '0.7rem' }} />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {formatNotificationTime(notification.createdAt)}
                                </Typography>
                              </Box>
                              {notification.title && (
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: notification.is_read ? 'text.secondary' : 'text.primary' }}>
                                  {notification.title}
                                </Typography>
                              )}
                              <Typography variant="body2" color={notification.is_read ? 'text.secondary' : 'text.primary'} sx={{ mb: 1 }}>
                                {notification.message}
                              </Typography>
                              <Typography
                                variant="caption"
                                color={notification.is_read ? 'text.disabled' : 'primary'}
                                sx={{
                                  fontStyle: 'italic',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {notification.comment && notification.comment.trim() ? 'Click to open evaluation form and view comment' : 'Click to open evaluation form'}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}

                {/* Chat Notifications */}
                {chatNotifications.map((notification) => (
                  <React.Fragment key={`chat-${notification.artifact_id}`}>
                    <ListItem
                      sx={{
                        backgroundColor: notification.read ? 'transparent' : 'rgba(0, 0, 0, 0.05)',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: notification.read ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.1)',
                        },
                        width: '100%',
                        mb: 1,
                        borderRadius: 1,
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                      }}
                      onClick={() => handleChatNotificationClick(notification)}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Chip label="New Comments" size="small" color={notification.read ? 'default' : 'secondary'} variant="outlined" />
                              </Box>
                              <Typography variant="body2" color={notification.read ? 'text.secondary' : 'text.primary'}>
                                {'You have '}
                                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                  {notification.count}
                                </Typography>
                                {` ${notification.count === 1 ? 'comment' : 'comments'} on `}
                                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                  {notification.artifact_name}
                                </Typography>
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Popover>

      {/* Comment Dialog */}
      <Dialog
        open={commentDialog.open}
        onClose={handleCloseCommentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            pb: 2,
          }}
        >
          Additional Comment
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pt: 2 }}>
            Notification: {commentDialog.message}
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              borderRadius: 1,
              border: '1px solid rgba(25, 118, 210, 0.2)',
            }}
          >
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {commentDialog.comment}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCloseCommentDialog} variant="contained" color="primary" sx={{ minWidth: 80, color: '#fff' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notification;
